import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { usePlatformHandoff } from "@/hooks/queries/platform-redirect/usePlatformRedirectQueries";
import { LAZO_USER_ROLES, PORTAL_ROLES } from "@/types/LazoUserRoles";
import { Box, Button, Link, Typography } from "@mui/material";
import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";

/**
 * The legacy-UI cutover seam for the whole `/one` section (mirrors LoginRouteGate's role for
 * `/login`): every way an external client can land in LazoOne — bookmark, post-login landing,
 * magic link, arrival from the auth app — funnels through this route element, so the redirect is a
 * property of *arriving at the section*, not of any particular link.
 *
 * The decision itself is authoritative server-side (`POST /api/platform-redirect/handoff`:
 * feature flag + tenant allowlist + memberships). This component only pre-filters internal users —
 * ops navigate `/one` legitimately for support and must never be asked — and obeys the answer.
 *
 * Fail-closed by decision of the rollout owner: if the flag says "cut over" but the handoff link
 * cannot be produced, the client sees a blocking screen with a retry — they do not silently keep
 * using the legacy UI. (A flag evaluation failure, by contrast, answers "stay" server-side, so an
 * incident in the flags platform never locks anyone out.)
 */

const INTERNAL_ROLE_NAMES = new Set<string>([...PORTAL_ROLES, LAZO_USER_ROLES.LAZO_PORTAL_EMPLOYEE]);

/**
 * True for Lazo staff. Permission first (`Lazo:Portal:Access`, same criterion as the `/portal`
 * route guard), then role names — `lazo-portal-viewer` / `lazo-portal-standard` carry no portal
 * permission yet are still internal accounts. The backend re-verifies with lazoTenant0 membership;
 * this pre-check only avoids minting handoff requests for people who can never be redirected.
 */
export function isInternalLazoUser(): boolean {
  if (AuthService.hasPermissionFor("Lazo:Portal:Access")) {
    return true;
  }
  const user = AuthService.getUser();
  return (user?.userRoles ?? []).some((role) => INTERNAL_ROLE_NAMES.has(role.name));
}

const FullPageMessage = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      textAlign: "center",
      px: 3,
    }}
  >
    {children}
  </Box>
);

export interface PlatformRedirectGateProps {
  /** Test seam (same convention as Logout): jsdom cannot perform a real cross-origin navigation. */
  navigateExternal?: (url: string) => void;
}

export const PlatformRedirectGate = ({ navigateExternal }: PlatformRedirectGateProps) => {
  const internal = isInternalLazoUser();
  // Where the visitor was headed. The gate sits on the way IN to `/one`, so this is still the URL
  // they followed — the task or document their e-mail named — and it is the only carrier of it.
  // Sending it lets the hand-off land there; without it every old link ended on the platform's
  // landing page, which is the bug this closes. The backend translates it through a closed table,
  // so an unmapped path degrades to that same landing page rather than being trusted.
  const { pathname, search } = useLocation();
  const { data, isPending, isError, isFetching, refetch } = usePlatformHandoff(!internal, `${pathname}${search}`);

  const handoffUrl = data?.redirect && data.url ? data.url : undefined;
  // redirect:true with no URL is a broken answer — treat it as the failure it is.
  const handoffBroken = isError || (data?.redirect === true && !data.url);

  useEffect(() => {
    if (handoffUrl) {
      if (navigateExternal) {
        navigateExternal(handoffUrl);
      } else {
        globalThis.location.replace(handoffUrl);
      }
    }
  }, [handoffUrl, navigateExternal]);

  if (internal) {
    return <Outlet />;
  }

  if (isPending && !handoffBroken) {
    // Same reasoning as LoginRouteGate: rendering the legacy UI for a beat and then yanking it
    // away reads as a flicker, and the person may have started working in it by then.
    return <Spinner sx={{ color: Colors.LAZO_PURPLE }} />;
  }

  if (handoffBroken) {
    return (
      <FullPageMessage>
        <Typography variant="h5">Lazo has moved to its new platform</Typography>
        <Typography color="text.secondary">
          We could not take you there automatically. Please try again — if the problem persists, contact your Lazo team.
        </Typography>
        <Button variant="contained" onClick={() => refetch()} disabled={isFetching} sx={{ bgcolor: Colors.LAZO_PURPLE }}>
          Try again
        </Button>
        <Link href={LazoPaths.LOGOUT}>Log out</Link>
      </FullPageMessage>
    );
  }

  if (handoffUrl) {
    return (
      <FullPageMessage>
        <Spinner sx={{ color: Colors.LAZO_PURPLE }} />
        <Typography variant="h5">Taking you to the new Lazo platform…</Typography>
        <Typography color="text.secondary">
          {/* Same URL the effect navigates with — its one-time code is single-use, so we never mint a second one here. */}
          If nothing happens, <Link href={handoffUrl}>continue to the new platform</Link>.
        </Typography>
      </FullPageMessage>
    );
  }

  return <Outlet />;
};
